'use client';

import './jumbotron-profound.scss';

import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';

import { Image } from '../../../atoms/image/image';
import { Video } from '../../../atoms/video/video';
import { FeatureBase } from '../../../molecules/feature-base/feature-base';
import {
  Lightbox,
  LightboxTrigger,
} from '../../../molecules/lightbox/lightbox';
import { addXFocal } from '../../../utils/image-utils';
import { getBaseSizeProps } from '../../../utils/text-utils';

export const JumbotronProfound: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  media,
  alt,
  mediaLogo,
  inline,
  items: _items,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <section
      className={cn('JumbotronProfound component--container flex relative', {
        'JumbotronProfound--alt': alt,
        'has-logo': mediaLogo,
        component: !inline,
      })}
    >
      <div className="JumbotronProfound__card w-full @sm:flex @sm:items-center @sm:justify-center">
        <div
          className={cn(
            'JumbotronProfound__media relative  @sm:absolute top-0 left-0 bottom-0 right-0',
            mediaLogo && 'hidden @sm:block',
          )}
        >
          {!media?.video && (
            <Image
              className="JumbotronProfound__media-img"
              image={addXFocal(media).image}
              hasPlaceholder
              priority={seoLevel === 1}
            />
          )}
          {media?.video && (
            <Video {...media.video} poster={media.image} isBackground />
          )}
          <div className="JumbotronProfound__overlay hidden @sm:block absolute top-0 left-0 bottom-0 right-0" />
        </div>

        <div className="JumbotronProfound__container flex flex-col-reverse @sm:flex-row @sm:justify-between w-full max-w-xl mt-comfy-xl @sm:mt-none">
          <div className="JumbotronProfound__content">
            <FeatureBase
              {...props}
              {...getBaseSizeProps(1)}
              seoLevel={seoLevel}
              inline
              buttonVariant="grow"
              programmaticButtons={
                media?.video && media.video.hasProgrammaticButton ? (
                  <Lightbox
                    key="jumbotron-profound-lightbox"
                    items={[
                      {
                        media,
                      },
                    ]}
                    startIndex={0}
                  >
                    <LightboxTrigger className="flex">
                      <ButtonStyle
                        iconBefore={<ArkIcon iconName="play-circle" />}
                        variant={props.secondaryCTA ? 'tertiary' : 'secondary'}
                      >
                        <span>{t('skippy.video.watch')}</span>
                      </ButtonStyle>
                    </LightboxTrigger>
                  </Lightbox>
                ) : undefined
              }
            />
          </div>
          {mediaLogo && (
            <div className="JumbotronProfound__logo">
              <Image
                className="JumbotronProfound__logo-img relative block"
                aspectRatio="16-9"
                rounded="md"
                image={mediaLogo.image}
                hasPlaceholder
                priority={seoLevel === 1}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
